/**
 * Formula text (docs/specs/database-formulas.md): the stored form with stable references, the typed text people see
 * and edit, and the A1 formula the engine runs.
 *
 * Stored references: `{propId}` this row, `{rowId:propId}` one cell, `{rowId:propId..rowId:propId}` a range,
 * `{*:propId}` a whole column. The grid behind A1 is the database's: column letters in property order (title = A),
 * row numbers in manual (rank) order. Typed text accepts `prop("Name")`, A1 cells/ranges/columns and property names
 * as column names (spaces → `_`). String literals are never touched. Pure; the engine itself runs on the server.
 */
import type { Database, PropertyDef, Row } from "@bundle/contracts";
import { compareRank } from "./database.ts";

/** Typed text longer than this is refused on save. */
export const FORMULA_MAX_LENGTH = 4000;
/** Formula + rollup properties per database. */
export const FORMULA_MAX_PROPERTIES = 30;
/** Databases with more rows don't calculate (cells show #ERROR!). */
export const FORMULA_MAX_ROWS = 25_000;
/** Rows × cells each column formula reads, per recalculation. */
export const FORMULA_MAX_CELLS_READ = 50_000_000;
/** Functions databases don't offer (volatile: every recalculation would change every row). */
export const FORMULA_BLOCKED_FUNCTIONS = ["RAND", "RANDBETWEEN", "RANDARRAY"];

/** A1 column letters for a 0-based index (0 → A, 25 → Z, 26 → AA). */
export function columnLetter(index: number): string {
  let n = index + 1;
  let out = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** 0-based column index of A1 letters (case-insensitive); -1 when not letters. */
export function columnIndex(letters: string): number {
  if (!/^[A-Za-z]{1,3}$/.test(letters)) return -1;
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** The property's column letter (its position in the database's property order), or "" when unknown. */
export function propertyLetter(db: Database, propertyId: string): string {
  const i = db.properties.findIndex((p) => p.id === propertyId);
  return i < 0 ? "" : columnLetter(i);
}

/** Row ids in the grid's order (rank, then id). Row n (1-based) is `ids[n - 1]`. */
export function gridRowIds(rows: Row[]): string[] {
  return rows.toSorted(compareRank).map((r) => r.id);
}

/** Row id → 1-based row number in the grid. */
export function rowNumbers(rows: Row[]): Record<string, number> {
  const out: Record<string, number> = {};
  gridRowIds(rows).forEach((id, i) => {
    out[id] = i + 1;
  });
  return out;
}

const A1_LIKE = /^[A-Za-z]{1,3}[0-9]+$/;

/**
 * The column name a property can be typed as: non-letters/digits become `_`, a leading digit gets `_`. Empty when it
 * would read as something else (an A1 cell like `Q4`, TRUE/FALSE, nothing left).
 */
export function columnName(name: string): string {
  let s = name.trim().replace(/[^\p{L}\p{N}_]/gu, "_");
  if (s === "" || /^_+$/.test(s)) return "";
  if (/^\p{N}/u.test(s)) s = "_" + s;
  if (A1_LIKE.test(s) || /^(TRUE|FALSE)$/i.test(s)) return "";
  return s;
}

// ---------- Tokenizing ----------

/** A piece of formula text: literal text, a string literal, or a stored reference (braces included). */
type Piece = { kind: "text" | "string" | "ref"; text: string };

/** Splits text into string literals ("…" with "" escapes), stored `{…}` references and everything else. */
function pieces(s: string): Piece[] {
  const out: Piece[] = [];
  let buf = "";
  let i = 0;
  const flush = () => {
    if (buf !== "") out.push({ kind: "text", text: buf });
    buf = "";
  };
  while (i < s.length) {
    const ch = s[i]!;
    if (ch === '"') {
      let j = i + 1;
      while (j < s.length) {
        if (s[j] === '"') {
          if (s[j + 1] === '"') j += 2;
          else break;
        } else j++;
      }
      flush();
      out.push({ kind: "string", text: s.slice(i, Math.min(j + 1, s.length)) });
      i = j + 1;
    } else if (ch === "{") {
      const end = s.indexOf("}", i);
      if (end < 0) {
        buf += s.slice(i);
        break;
      }
      flush();
      out.push({ kind: "ref", text: s.slice(i, end + 1) });
      i = end + 1;
    } else {
      buf += ch;
      i++;
    }
  }
  flush();
  return out;
}

/** A parsed stored reference. */
export type FormulaRef =
  | { kind: "this"; propertyId: string }
  | { kind: "cell"; rowId: string; propertyId: string }
  | { kind: "range"; from: { rowId: string; propertyId: string }; to: { rowId: string; propertyId: string } }
  | { kind: "column"; propertyId: string };

/** Parses `{…}` (braces included); undefined when it isn't a reference. */
export function parseRef(text: string): FormulaRef | undefined {
  const inner = text.slice(1, -1);
  if (inner === "") return undefined;
  const cell = (s: string) => {
    const k = s.indexOf(":");
    return k <= 0 || k === s.length - 1 ? undefined : { rowId: s.slice(0, k), propertyId: s.slice(k + 1) };
  };
  const dots = inner.indexOf("..");
  if (dots >= 0) {
    const from = cell(inner.slice(0, dots));
    const to = cell(inner.slice(dots + 2));
    return from && to ? { kind: "range", from, to } : undefined;
  }
  if (inner.startsWith("*:")) return inner.length > 2 ? { kind: "column", propertyId: inner.slice(2) } : undefined;
  const c = cell(inner);
  if (c) return { kind: "cell", ...c };
  return /^[^\s:{}]+$/.test(inner) ? { kind: "this", propertyId: inner } : undefined;
}

function refText(r: FormulaRef): string {
  switch (r.kind) {
    case "this":
      return `{${r.propertyId}}`;
    case "cell":
      return `{${r.rowId}:${r.propertyId}}`;
    case "range":
      return `{${r.from.rowId}:${r.from.propertyId}..${r.to.rowId}:${r.to.propertyId}}`;
    case "column":
      return `{*:${r.propertyId}}`;
  }
}

/** The references a stored expression holds, in order. */
export function formulaRefs(expression: string): FormulaRef[] {
  const out: FormulaRef[] = [];
  for (const p of pieces(expression)) {
    if (p.kind !== "ref") continue;
    const r = parseRef(p.text);
    if (r) out.push(r);
  }
  return out;
}

/** Upper-cased names of the functions an expression calls (outside string literals), first use order. */
export function formulaFunctions(expression: string): string[] {
  const out: string[] = [];
  for (const p of pieces(expression)) {
    if (p.kind !== "text") continue;
    for (const m of p.text.matchAll(/([A-Za-z_][A-Za-z0-9_.]*)\s*\(/g)) {
      const name = m[1]!.toUpperCase();
      if (name !== "PROP" && !out.includes(name)) out.push(name);
    }
  }
  return out;
}

// ---------- Typed text → stored form ----------

const CELL = String.raw`\$?([A-Za-z]{1,3})\$?([0-9]{1,7})`;
/** A1 range/cell, column range, or an identifier, at a word boundary. */
const TOKEN = new RegExp(
  String.raw`(?<![\p{L}\p{N}_.$!])(?:${CELL}:${CELL}|${CELL})(?![\p{L}\p{N}_(!])|` + // 1-6: range or cell
    String.raw`(?<![\p{L}\p{N}_.$!])\$?([A-Za-z]{1,3}):\$?([A-Za-z]{1,3})(?![\p{L}\p{N}_(!])|` + // 7-8: column range
    String.raw`(?<![\p{L}\p{N}_.$!])([\p{L}_][\p{L}\p{N}_]*)(?![\p{L}\p{N}_.(!])`, // 9: identifier
  "giu",
);

/**
 * Turns typed text into the stored form, resolving names against `db` and A1 against the grid (`rows` in any order).
 * Unknown A1 cells (past the last column or row) become `#REF!`; unknown prop("…") names stay as typed (they
 * evaluate to #NAME?); identifiers that aren't column names stay as typed. Stored references pass through.
 */
export function parseFormulaText(text: string, db: Database, rows: Row[]): string {
  const ids = gridRowIds(rows);
  const byName = new Map<string, PropertyDef>();
  const byColumnName = new Map<string, PropertyDef>();
  for (const p of db.properties) {
    if (!byName.has(p.name.toLowerCase())) byName.set(p.name.toLowerCase(), p);
    const cn = columnName(p.name).toLowerCase();
    if (cn !== "" && !byColumnName.has(cn)) byColumnName.set(cn, p);
  }
  const prop = (letters: string) => db.properties[columnIndex(letters)];
  const rowId = (n: string) => ids[Number(n) - 1];
  // prop("Name") spans a text piece, a string literal and a text piece: resolve it before the rest.
  const list = pieces(text);
  for (let i = 0; i + 2 < list.length; i++) {
    const before = list[i]!;
    const str = list[i + 1]!;
    const after = list[i + 2]!;
    const open = before.kind === "text" ? /(?<![\p{L}\p{N}_.])prop\s*\(\s*$/iu.exec(before.text) : null;
    const close = after.kind === "text" ? /^\s*\)/.exec(after.text) : null;
    if (!open || str.kind !== "string" || !close) continue;
    const p = byName.get(str.text.slice(1, -1).replace(/""/g, '"').toLowerCase());
    if (!p) continue;
    before.text = before.text.slice(0, open.index);
    list.splice(i + 1, 1, { kind: "ref", text: `{${p.id}}` });
    after.text = after.text.slice(close[0].length);
  }
  let out = "";
  for (const piece of list) {
    if (piece.kind !== "text") {
      out += piece.text;
      continue;
    }
    out += piece.text.replace(TOKEN, (m: string, c1?: string, r1?: string, c2?: string, r2?: string, c3?: string, r3?: string, k1?: string, k2?: string, ident?: string) => {
      if (c1 !== undefined && r1 !== undefined && c2 !== undefined && r2 !== undefined) {
        const pa = prop(c1);
        const pb = prop(c2);
        const ra = rowId(r1);
        const rb = rowId(r2);
        return pa && pb && ra && rb ? `{${ra}:${pa.id}..${rb}:${pb.id}}` : "#REF!";
      }
      if (c3 !== undefined && r3 !== undefined) {
        const p = prop(c3);
        const r = rowId(r3);
        return p && r ? `{${r}:${p.id}}` : "#REF!";
      }
      if (k1 !== undefined && k2 !== undefined) {
        // Whole columns one at a time (C:C); multi-column ranges have no stable form yet.
        const pa = prop(k1);
        return pa && columnIndex(k1) === columnIndex(k2) ? `{*:${pa.id}}` : "#REF!";
      }
      if (ident !== undefined) {
        const p = byColumnName.get(ident.toLowerCase());
        return p ? `{*:${p.id}}` : m;
      }
      return m;
    });
  }
  return out;
}

// ---------- Stored form → typed text ----------

/** The stored expression as people see and edit it (missing properties or rows read `#REF!`). */
export function formulaDisplayText(expression: string, db: Database, rows: Row[]): string {
  const numbers = rowNumbers(rows);
  const letter = (propertyId: string) => propertyLetter(db, propertyId);
  const cellText = (rowId: string, propertyId: string): string | undefined => {
    const l = letter(propertyId);
    const n = numbers[rowId];
    return l === "" || n === undefined ? undefined : `${l}${n}`;
  };
  let out = "";
  for (const piece of pieces(expression)) {
    if (piece.kind !== "ref") {
      out += piece.text;
      continue;
    }
    const r = parseRef(piece.text);
    if (r === undefined) {
      out += piece.text;
      continue;
    }
    switch (r.kind) {
      case "this": {
        const p = db.properties.find((x) => x.id === r.propertyId);
        out += p ? `prop("${p.name.replace(/"/g, '""')}")` : "#REF!";
        break;
      }
      case "cell":
        out += cellText(r.rowId, r.propertyId) ?? "#REF!";
        break;
      case "range": {
        const a = cellText(r.from.rowId, r.from.propertyId);
        const b = cellText(r.to.rowId, r.to.propertyId);
        out += a && b ? `${a}:${b}` : "#REF!";
        break;
      }
      case "column": {
        const p = db.properties.find((x) => x.id === r.propertyId);
        if (!p) {
          out += "#REF!";
          break;
        }
        const cn = columnName(p.name);
        const clash = cn !== "" && db.properties.some((x) => x.id !== p.id && columnName(x.name).toLowerCase() === cn.toLowerCase());
        out += cn !== "" && !clash ? cn : `${letter(p.id)}:${letter(p.id)}`;
        break;
      }
    }
  }
  return out;
}

// ---------- Stored form → engine A1 ----------

/**
 * The A1 formula (no leading "=") the engine runs in grid row `row` (1-based). `columnOf` maps a property id to its
 * 0-based engine column, `rowOf` a row id to its 1-based engine row; references they don't know become `#REF!`. A
 * column is the whole column only as a function argument; elsewhere it's this row's cell (`@`).
 */
export function engineFormula(expression: string, row: number, columnOf: Record<string, number>, rowOf: Record<string, number>): string {
  const col = (propertyId: string) => {
    const i = columnOf[propertyId];
    return i === undefined ? undefined : columnLetter(i);
  };
  const list = pieces(expression);
  let out = "";
  list.forEach((piece, i) => {
    if (piece.kind !== "ref") {
      out += piece.text;
      return;
    }
    const r = parseRef(piece.text);
    if (r === undefined) {
      out += piece.text;
      return;
    }
    switch (r.kind) {
      case "this": {
        const l = col(r.propertyId);
        out += l === undefined ? "#REF!" : `$${l}$${row}`;
        break;
      }
      case "cell": {
        const l = col(r.propertyId);
        const n = rowOf[r.rowId];
        out += l === undefined || n === undefined ? "#REF!" : `$${l}$${n}`;
        break;
      }
      case "range": {
        const ca = columnOf[r.from.propertyId];
        const cb = columnOf[r.to.propertyId];
        const ra = rowOf[r.from.rowId];
        const rb = rowOf[r.to.rowId];
        if (ca === undefined || cb === undefined || ra === undefined || rb === undefined) {
          out += "#REF!";
          break;
        }
        out += `$${columnLetter(Math.min(ca, cb))}$${Math.min(ra, rb)}:$${columnLetter(Math.max(ca, cb))}$${Math.max(ra, rb)}`;
        break;
      }
      case "column": {
        // A whole column as a function argument (SUM(Price)); anywhere else this row's cell, by implicit
        // intersection (Price * 2), as in Sheets and Excel.
        const l = col(r.propertyId);
        const before = list[i - 1]?.kind === "text" ? list[i - 1]!.text.trimEnd().at(-1) : undefined;
        const after = list[i + 1]?.kind === "text" ? list[i + 1]!.text.trimStart()[0] : undefined;
        const argument = (before === "(" || before === ",") && (after === ")" || after === ",");
        out += l === undefined ? "#REF!" : `${argument ? "" : "@"}$${l}:$${l}`;
        break;
      }
    }
  });
  return out;
}

/**
 * Cells one evaluation of the expression reads (this row and single cells 1, ranges their area, columns `rowCount`);
 * unknown references count 0. The cost of a column formula is this × the row count.
 */
export function formulaCellsRead(expression: string, rowCount: number, columnOf: Record<string, number>, rowOf: Record<string, number>): number {
  let n = 0;
  for (const r of formulaRefs(expression)) {
    switch (r.kind) {
      case "this":
      case "cell":
        n += 1;
        break;
      case "column":
        n += rowCount;
        break;
      case "range": {
        const ca = columnOf[r.from.propertyId];
        const cb = columnOf[r.to.propertyId];
        const ra = rowOf[r.from.rowId];
        const rb = rowOf[r.to.rowId];
        if (ca !== undefined && cb !== undefined && ra !== undefined && rb !== undefined) n += (Math.abs(ca - cb) + 1) * (Math.abs(ra - rb) + 1);
        break;
      }
    }
  }
  return n;
}

/**
 * Keeps a stored expression valid after rows are deleted (Sheets): a deleted end row of a range moves to the next
 * surviving row inside the range (grid order `before`, the rows as they were); a range with no rows left, and a cell
 * of a deleted row, become `#REF!`. Unchanged when nothing it references was deleted.
 */
export function dropRowsFromFormula(expression: string, before: string[], deleted: string[]): string {
  if (deleted.length === 0) return expression;
  const gone = new Set(deleted);
  let out = "";
  for (const piece of pieces(expression)) {
    const r = piece.kind === "ref" ? parseRef(piece.text) : undefined;
    if (r === undefined || (r.kind !== "cell" && r.kind !== "range")) {
      out += piece.text;
      continue;
    }
    if (r.kind === "cell") {
      out += gone.has(r.rowId) ? "#REF!" : piece.text;
      continue;
    }
    if (!gone.has(r.from.rowId) && !gone.has(r.to.rowId)) {
      out += piece.text;
      continue;
    }
    const a = before.indexOf(r.from.rowId);
    const b = before.indexOf(r.to.rowId);
    if (a < 0 || b < 0) {
      out += "#REF!";
      continue;
    }
    const inside = before.slice(Math.min(a, b), Math.max(a, b) + 1).filter((id) => !gone.has(id));
    if (inside.length === 0) {
      out += "#REF!";
      continue;
    }
    const first = inside[0]!;
    const last = inside[inside.length - 1]!;
    const [fromRow, toRow] = a <= b ? [first, last] : [last, first];
    out += refText({ kind: "range", from: { rowId: fromRow, propertyId: r.from.propertyId }, to: { rowId: toRow, propertyId: r.to.propertyId } });
  }
  return out;
}
